/**
 * External links utility functions
 * Generates URLs for various bioinformatics databases
 */

// MIBiG Repository
export function getMibigUrl(accession: string, version?: string): string {
  const ver = version || '5'
  return `https://mibig.secondarymetabolites.org/repository/${accession}.${ver}`
}

// NCBI Taxonomy Browser
export function getNcbiTaxonomyUrl(taxId: string | number): string {
  return `https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${taxId}`
}

// NCBI Protein
export function getNcbiProteinUrl(proteinId: string): string {
  return `https://www.ncbi.nlm.nih.gov/protein/${proteinId}`
}

// NCBI Nucleotide
export function getNcbiNucleotideUrl(accession: string): string {
  return `https://www.ncbi.nlm.nih.gov/nuccore/${accession}`
}

// UniProt
export function getUniprotUrl(uniprotId: string): string {
  return `https://www.uniprot.org/uniprotkb/${uniprotId}/entry`
}

// PubChem Compound
export function getPubchemUrl(compoundId: string | number): string {
  return `https://pubchem.ncbi.nlm.nih.gov/compound/${compoundId}`
}

// NP Atlas (Natural Products Atlas)
export function getNpAtlasUrl(npaId: string): string {
  // Handle both formats: "NPA01961" and "npatlas:NPA01961"
  const id = npaId.replace('npatlas:', '')
  return `https://www.npatlas.org/explore/compounds/${id}`
}

// ChemSpider
export function getChemSpiderUrl(chemspiderId: string | number): string {
  // Handle format: "chemspider:19955692"
  const id = String(chemspiderId).replace('chemspider:', '')
  return `https://www.chemspider.com/${id}`
}

// ChEMBL
export function getChemblUrl(chemblId: string): string {
  // Handle format: "chembl:CHEMBL1651097"
  const id = chemblId.replace('chembl:', '')
  return `https://www.ebi.ac.uk/chembl/compound_report_card/${id}`
}

// NCBI CDD (Conserved Domain Database)
export function getNcbiCddUrl(pssmId: string | number): string {
  return `https://www.ncbi.nlm.nih.gov/Structure/cdd/cddsrv.cgi?uid=${pssmId}`
}

// Pfam
export function getPfamUrl(pfamId: string): string {
  // Handle format: "pfam18678" or "PF18678"
  const id = pfamId.replace('pfam', 'PF')
  return `https://www.ebi.ac.uk/interpro/entry/pfam/${id}`
}

// PDB (RCSB Protein Data Bank)
export function getPdbUrl(pdbId: string): string {
  return `https://www.rcsb.org/structure/${pdbId.toUpperCase()}`
}

// AlphaFold DB
export function getAlphaFoldUrl(uniprotId: string): string {
  return `https://alphafold.ebi.ac.uk/entry/${uniprotId}`
}

// Rhea (Biochemical Reactions Database)
export function getRheaUrl(rheaId: string | number): string {
  return `https://www.rhea-db.org/rhea/${rheaId}`
}

// EC Number (ExPASy ENZYME)
export function getEcUrl(ecNumber: string): string {
  return `https://enzyme.expasy.org/EC/${ecNumber}`
}

// InterPro
export function getInterProUrl(iprId: string): string {
  return `https://www.ebi.ac.uk/interpro/entry/InterPro/${iprId}`
}

// KEGG Compound
export function getKeggCompoundUrl(keggId: string): string {
  return `https://www.genome.jp/dbget-bin/www_bget?cpd:${keggId}`
}

/**
 * Parse database_id array and return appropriate URL
 * Handles formats like: "pubchem:71455791", "npatlas:NPA01961", etc.
 */
export function parseCompoundDatabaseId(databaseId: string): { type: string; id: string; url: string } | null {
  if (!databaseId) return null

  const [type, id] = databaseId.split(':')

  switch (type.toLowerCase()) {
    case 'pubchem':
      return { type: 'PubChem', id, url: getPubchemUrl(id) }
    case 'npatlas':
      return { type: 'NP Atlas', id, url: getNpAtlasUrl(id) }
    case 'chemspider':
      return { type: 'ChemSpider', id, url: getChemSpiderUrl(id) }
    case 'chembl':
      return { type: 'ChEMBL', id, url: getChemblUrl(databaseId) }
    case 'chebi':
      return { type: 'ChEBI', id, url: `https://www.ebi.ac.uk/chebi/searchId.do?chebiId=CHEBI:${id}` }
    case 'kegg':
      return { type: 'KEGG', id, url: getKeggCompoundUrl(id) }
    default:
      return null
  }
}

/**
 * Get all external links for a compound's database_id array
 */
export function getCompoundLinks(databaseIds: string[] | undefined): Array<{ type: string; id: string; url: string }> {
  if (!databaseIds || databaseIds.length === 0) return []

  return databaseIds
    .map(parseCompoundDatabaseId)
    .filter((link): link is { type: string; id: string; url: string } => link !== null)
}

/**
 * Get the primary/preferred link for a compound
 * Priority: NP Atlas > PubChem > ChEMBL > others
 */
export function getPrimaryCompoundLink(databaseIds: string[] | undefined): { type: string; id: string; url: string } | null {
  if (!databaseIds || databaseIds.length === 0) return null

  const links = getCompoundLinks(databaseIds)
  if (links.length === 0) return null

  // Priority order
  const priority = ['NP Atlas', 'PubChem', 'ChEMBL', 'ChemSpider', 'ChEBI', 'KEGG']

  for (const pType of priority) {
    const found = links.find(l => l.type === pType)
    if (found) return found
  }

  return links[0]
}

// Export all functions as a namespace
export const externalLinks = {
  mibig: getMibigUrl,
  ncbiTaxonomy: getNcbiTaxonomyUrl,
  ncbiProtein: getNcbiProteinUrl,
  ncbiNucleotide: getNcbiNucleotideUrl,
  uniprot: getUniprotUrl,
  pubchem: getPubchemUrl,
  npAtlas: getNpAtlasUrl,
  chemSpider: getChemSpiderUrl,
  chembl: getChemblUrl,
  ncbiCdd: getNcbiCddUrl,
  pfam: getPfamUrl,
  pdb: getPdbUrl,
  alphaFold: getAlphaFoldUrl,
  rhea: getRheaUrl,
  ec: getEcUrl,
  interPro: getInterProUrl,
  kegg: getKeggCompoundUrl,
}

export default externalLinks
